import { supabase, isSupabaseConfigured } from '@/lib/supabase'
import type {
  WritingContext,
  SecretNudge,
  StorySecret,
  WritingSceneType,
} from '@/types/storytelling'
import type { Json } from '@/types/database'

export interface SecretWithMeta {
  secretId: string
  title: string
  principle: string
  metadata: {
    category?: string
    triggerContexts?: WritingSceneType[]
  }
}

/** Suggestion templates keyed by secret ID */
const suggestionTemplates: Record<string, Record<WritingSceneType, string>> = {
  'the-hook': {
    opening: 'This is your opening. What if you started with the most interesting moment instead of building up to it?',
    middle: 'The reader might be drifting. Can you hook them back in with something unexpected?',
    climax: 'Big moment coming. Make sure the reader is locked in.',
    resolution: 'Even endings can hook — leave the reader thinking.',
  },
  'characters-that-breathe': {
    opening: 'First time the reader meets your character. Skip the labels — give them one tiny weird thing only this kid would do.',
    middle: 'Your character\'s mid-action. Slip in one specific small thing in how they do it — something only they\'d do that way.',
    climax: 'Big moment. Make sure your character is still themselves here — that specific thing you gave them at the start should still be true.',
    resolution: 'End of the story. Is your character still doing that one specific thing — or did they drift into generic somewhere along the way?',
  },
}

/**
 * Match unlocked secrets to the current writing context.
 * Pure function — no DB calls.
 */
export function getRelevantSecrets(
  secrets: SecretWithMeta[],
  context: WritingContext
): SecretNudge[] {
  if (!context.sceneType) return []

  const nudges: SecretNudge[] = []

  for (const s of secrets) {
    const triggers = s.metadata.triggerContexts
    if (!triggers || !triggers.includes(context.sceneType)) continue

    const template = suggestionTemplates[s.secretId]
    const suggestion = template?.[context.sceneType] ?? `Remember: ${s.principle}`

    nudges.push({
      secret: {
        id: s.secretId,
        title: s.title,
        principle: s.principle,
        metadata: {
          category: s.metadata.category as StorySecret['metadata']['category'],
          triggerContexts: s.metadata.triggerContexts,
        },
      },
      suggestion,
    })
  }

  return nudges
}

/**
 * Load unlocked secrets for a child from Supabase.
 */
export async function loadSecrets(childId: string): Promise<SecretWithMeta[]> {
  if (!isSupabaseConfigured()) return []

  const { data, error } = await supabase
    .from('story_secrets')
    .select('*')
    .eq('child_id', childId)

  if (error) {
    console.error('Failed to load story secrets:', error)
    return []
  }

  return (data ?? []).map((row) => {
    const meta = (row.metadata ?? {}) as Record<string, Json>
    return {
      secretId: row.secret_id,
      title: row.secret_id.replace(/-/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()),
      principle: row.principle_text,
      metadata: {
        category: meta.category as string | undefined,
        triggerContexts: meta.triggerContexts as WritingSceneType[] | undefined,
      },
    }
  })
}

/**
 * Unlock a secret for a child.
 */
export async function unlockSecret(
  childId: string,
  secret: StorySecret,
  chapterId: string
): Promise<boolean> {
  if (!isSupabaseConfigured()) return false

  const { error } = await supabase
    .from('story_secrets')
    .upsert(
      {
        child_id: childId,
        secret_id: secret.id,
        chapter_id: chapterId,
        principle_text: secret.principle,
        metadata: secret.metadata as Json,
      },
      { onConflict: 'child_id,secret_id' }
    )

  if (error) {
    console.error('Failed to unlock secret:', error)
    return false
  }

  return true
}
